import React, { useState, useEffect, useMemo } from "react";
import SpotifyIcon from "../../../../assets/images/icons/spotify-white.png";
import AppleMusicIcon from "../../../../assets/images/icons/apple-music.png";
import TidalIcon from "../../../../assets/images/icons/tidal.png";
import DeezerIcon from "../../../../assets/images/icons/deezer.png";
import AmazonIcon from "../../../../assets/images/icons/amazon-music.png";
import AudiomackIcon from "../../../../assets/images/icons/audio-mack.png";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import {
    useArtistFutureSave,
    useUpdateArtistFutureSave
} from "../../../../queries/artistManger/smartLink.query";
import { ConfirmModal } from "../../ConfirmModal";

const services = [
    { name: "Spotify", image: SpotifyIcon },
    { name: "Apple Music", image: AppleMusicIcon },
    { name: "TIDAL", image: TidalIcon },
    { name: "Deezer", image: DeezerIcon },
    { name: "Amazon Music (Streaming)", image: AmazonIcon },
    { name: "Audiomack", image: AudiomackIcon },
];

const SERVICE_KEY_MAP = {
    spotify: 0,
    apple: 1,
    tidal: 2,
    deezer: 3,
    amazon: 4,
    audiomack: 5
};

const SERVICE_KEYS = [
    "spotify",
    "apple",
    "tidal",
    "deezer",
    "amazon",
    "audiomack"
];

export const FutureSave = ({ queriesEnabled = true }) => {
    const { data, isLoading } = useArtistFutureSave({ enabled: queriesEnabled });
    const { mutate: updateFutureSave, isPending } = useUpdateArtistFutureSave();
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [switchStatus, setSwitchStatus] = useState(false);
    const [profiles, setProfiles] = useState(services.map(() => ""));
    const [initialState, setInitialState] = useState({
        enabled: false,
        services: services.map(() => "")
    });

    useEffect(() => {
        if (!data) return;

        setSwitchStatus(!!data.enabled);

        const updatedProfiles = services.map(() => "");

        data.services?.forEach((service) => {
            const index = SERVICE_KEY_MAP[service.service];
            if (index !== undefined) {
                updatedProfiles[index] = service.link || "";
            }
        });

        setProfiles(updatedProfiles);

        setInitialState({
            enabled: !!data.enabled,
            services: updatedProfiles
        });

    }, [data]);

    const handleChange = (index, value) => {
        const updated = [...profiles];
        updated[index] = value;
        setProfiles(updated);
    };

    // Dirty detection
    const isDirty = useMemo(() => {
        if (initialState.enabled !== switchStatus) return true;

        return initialState.services.some(
            (link, i) => link !== profiles[i]
        );
    }, [initialState, switchStatus, profiles]);

    const saveFutureSave = () => {
        const formattedServices = profiles
            .map((link, index) => ({
                service: SERVICE_KEYS[index],
                link: link?.trim()
            }))
            .filter(item => item.link);

        updateFutureSave({
            enabled: switchStatus,
            services: formattedServices
        });
    };

    const handleSave = () => {
        if (!switchStatus) {
            setShowDisableModal(true);
            return;
        }
        saveFutureSave();
    };

    return (
        <div className="flex flex-col lg:flex-row gap-6">
            {/* LEFT SECTION */}
            <div id="tour-future-links" className="flex-1 bg-white border rounded-xl p-4 md:p-6">
                <div className="heading-wrapper mb-5">
                    <div className="heading text-[22px] font-bold text-theme_clr1 mb-2">
                        Future Save Setup
                    </div>
                    <div className="text-sm text-theme_clr1">
                        Let fans subscribe to receive all future Pre-Saves by opting-in just once. When enabled, fans can
                        subscribe on any of the services where you provide an artist URL below. The Future Save helps you
                        reach as many fans in your audience when you have a new release.
                    </div>
                </div>

                <div className="switch-wrapper flex items-center justify-between gap-3 mb-6">
                    <span className="label font-semibold text-theme_clr1 text-base">
                        Status
                    </span>
                    <ToggleSwitch
                        label=""
                        enabled={switchStatus}
                        setEnabled={setSwitchStatus}
                        size="md"
                    />
                </div>

                <div className="divider-wrapper relative text-center mb-4">
                    <h3 className="bg-white inline-block relative text-[10px] font-semibold text-theme_clr1 z-10 px-4">
                        FUTURE SAVE SERVICES
                    </h3>
                    <div className="h-[1px] w-full bg-theme_clr1 absolute top-1/2 -translate-y-1/2 z-0"></div>
                </div>

                <div className="space-y-5">
                    {(isLoading || isPending) ? (
                        services.map((_, index) => (
                            <div key={index} className="animate-pulse">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="h-[30px] w-[30px] bg-gray-200 rounded"></div>
                                    <div className="h-4 w-32 bg-gray-200 rounded"></div>
                                </div>
                                <div className="h-3 w-28 bg-gray-200 rounded mb-2"></div>
                                <div className="flex items-center gap-2">
                                    <div className="h-12 w-full bg-gray-200 rounded-md"></div>
                                    <div className="h-12 w-[50px] bg-gray-200 rounded-md"></div>
                                </div>
                            </div>
                        ))
                    ) : (
                        services.map((service, index) => (
                            <div key={service.name}>
                                <div className="flex items-center gap-3 mb-3">
                                    <img src={service.image} alt="Icon" className="h-[30px] w-[30px]" />
                                    <span className="font-semibold text-theme_clr1">
                                        {service.name}
                                    </span>
                                </div>

                                <label className="font-inter font-semibold text-xs text-theme_clr1 block mb-1">
                                    Artist Profile Link
                                </label>

                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        placeholder="Enter artist profile link"
                                        value={profiles[index]}
                                        onChange={(e) => handleChange(index, e.target.value)}
                                        disabled={!switchStatus}
                                        className="w-full border border-theme_clr2 rounded-md p-3 text-base text-theme_clr1 placeholder:text-gray-600 focus:outline-none focus:ring-1 focus:ring-theme_clr4 disabled:bg-gray-100"
                                    />

                                    <button
                                        onClick={() => handleChange(index, "")}
                                        disabled={!switchStatus}
                                        className="bg-theme_clr2 hover:bg-theme_clr4 min-w-[50px] text-white rounded-md p-3 transition disabled:opacity-50"
                                    >
                                        <i className="bi bi-x-lg text-lg text-white leading-tight"></i>
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="mt-6 flex justify-end">
                    <button
                        onClick={handleSave}
                        disabled={isPending || !isDirty}
                        className="bg-theme_clr2 hover:bg-theme_clr4 text-white px-6 py-2 rounded-md transition disabled:opacity-50"
                    >
                        {isPending ? "Saving..." : "Save Changes"}
                    </button>
                </div>
            </div>

            {/* RIGHT SECTION (UNCHANGED) */}
            <div id="tour-future-links-support" className="w-full lg:w-80 flex flex-col gap-4">
                <div className="bg-white border rounded-xl p-4 md:p-5">
                    <div className="font-semibold text-theme_clr1 text-sm mb-2">
                        How do Future Save fans get my new music?
                    </div>
                    <div className="text-xs text-theme_clr1 text-opacity-50">
                        Once enabled, you will find the ability to activate the Future
                        Save on any individual Pre-Save you create or edit.
                    </div>
                </div>

                <div className="bg-white border rounded-xl p-4 md:p-5">
                    <div className="font-semibold text-theme_clr1 text-sm mb-2">
                        Where do I see my Future Save fans?
                    </div>
                    <div className="text-xs text-theme_clr1 text-opacity-50">
                        View your total Future Save fans from the Audience tab.
                    </div>
                </div>

                <div className="bg-white border rounded-xl p-4 md:p-5">
                    <div className="font-semibold text-theme_clr1 text-sm mb-2">
                        Need more help?
                    </div>
                    <button className="flex items-center gap-2 text-sm text-theme_clr2 hover:text-theme_clr4">
                        <i className="bi bi-chat-dots text-theme_clr2 text-2xl"></i>
                        Chat with support
                    </button>
                </div>

                <div className="bg-theme_clr2 bg-opacity-30 border border-theme_clr2 rounded-xl p-4 md:p-5">
                    <h4 className="font-semibold text-theme_clr1 text-sm mb-1">
                        Note
                    </h4>
                    <div className="text-xs text-theme_clr1">
                        The profiles you submit will be used for ID Match Verification.
                    </div>
                </div>
            </div>
            <ConfirmModal
                isOpen={showDisableModal}
                title="Disable Future Save?"
                description="Fans will no longer automatically receive your future releases. Are you sure you want to disable Future Save?"
                confirmText="Disable"
                cancelText="Cancel"
                danger
                isLoading={isPending}
                onCancel={() => setShowDisableModal(false)}
                onConfirm={() => {
                    saveFutureSave();
                    setShowDisableModal(false);
                }}
            />
        </div>
    );
};